import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import VoucherCard from "../../components/Common/VoucherCard";
import VoucherDetailModal from "../../components/Common/VoucherDetailModal";
import RedeemVoucherModal from "../../components/Common/RedeemVoucherModal";
import EmptyState from "../../components/Common/EmptyState";

//Import API
import endpoints from "../../api/endpoints";
import { get } from "../../api/axios";

//Utils
import { extractErrorMessage } from "../../utils/errorHandler";

//Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";


/* -------------------------------------------------------------------------
 * VouchersPage — "Vouchers" list page: title, All/Redeemed/Expired filter
 * tabs, and a responsive wrapping grid of VoucherCard.
 * ---------------------------------------------------------------------- */

const FILTERS = [
  { id: "active", label: "Active" },
  { id: "redeemed", label: "Redeemed" },
  { id: "expired", label: "Expired" },
];

const VouchersPage = ({ vouchers, onRedeemVoucher, onVoucherClick }) => {
  document.title = "Vouchers | Omaha Food Lovers";
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState("active");
  const [selectedVoucherId, setSelectedVoucherId] = useState(null);
  const [isVoucherModalOpen, setIsVoucherModalOpen] = useState(false);
  const [userVouchers, setUserVouchers] = useState(vouchers || []);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollRef = useRef(null);

  const handleNavigateToWallet = () => {
    navigate("/wallet");
  };

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Fetch vouchers from API
  const fetchVouchers = useCallback(async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setIsLoading(true);
    }

    try {
      const apiUrl = `${endpoints.vouchers}?page=${pageNum}&page_size=16&status=${activeFilter}`;
      
      const response = await get(apiUrl);
      const apiVouchers = response.data.data?.data || [];
      const currentPage = response.data.data?.current_page || 1;
      const lastPage = response.data.data?.last_page || 1;

      // Transform API data to match VoucherCard format
      const transformedVouchers = apiVouchers.map(voucher => {
        // Determine status based on API fields
        let status = "active";
        let statusDate = "";

        if (voucher.redeemed_at) {
          status = "redeemed";
          statusDate = voucher.redeemed_at;
        } else if (voucher.expires_at && new Date(voucher.expires_at) < new Date()) {
          status = "expired";
          statusDate = voucher.expires_at;
        } else if (voucher.expires_at) {
          status = "active";
          statusDate = voucher.expires_at;
        }

        return {
          id: voucher.id,
          amount: voucher.amount_cents ? voucher.amount_cents / 100 : voucher.amount || 0,
          status: status,
          statusDate: statusDate,
          restaurantName: voucher.vendor_listing?.title || voucher.restaurant?.name || voucher.restaurant_name || "Restaurant",
          expiresLabel: statusDate,
          expiresSubLabel: status === "active" ? "Valid" : status === "redeemed" ? "Redeemed" : "Expired",
          qrCodeUrl: voucher.qr_url || voucher.qr_code_url || "",
          voucherId: voucher.code || voucher.id?.toString(),
        };
      });

      if (isLoadMore) {
        setUserVouchers((prev) => [...prev, ...transformedVouchers]);
      } else {
        setUserVouchers(transformedVouchers);
      }

      setHasMore(currentPage < lastPage);
      setCurrentPage(currentPage);
    } catch (err) {
      console.error("Failed to fetch vouchers:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
      if (!isLoadMore) {
        setUserVouchers([]);
      }
    } finally {
      setIsLoading(false);
      setLoadingMore(false);
    }
  }, [activeFilter]);

  // Fetch vouchers on mount + whenever filter changes
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    fetchVouchers(1, false);
  }, [activeFilter, fetchVouchers]);

  // Handle scroll for infinite scroll (same pattern as DataTable/ExplorePartnersSection)
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    console.log('Vouchers Scroll:', {
      scrollTop,
      scrollHeight,
      clientHeight,
      scrollPercentage: scrollPercentage.toFixed(2),
      hasMore,
      loadingMore,
      isScrolling,
      currentPage,
      voucherCount: userVouchers.length
    });

    // Load more when scrolled to 90% of the bottom
    if (hasMore && !loadingMore && !isScrolling && scrollPercentage >= 0.9) {
      console.log('Triggering load more...');
      setIsScrolling(true);
      fetchVouchers(currentPage + 1, true);
      setTimeout(() => setIsScrolling(false), 500);
    }
  };

  // Fetch points from API
  const fetchPoints = useCallback(async () => {
    try {
      const response = await get(endpoints.pointsBalance);
      const apiPoints = response.data.data?.points || 0;
      setUserPoints(apiPoints);
    } catch (err) {
      console.error("Failed to fetch points:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
      setUserPoints(0);
    }
  }, []);

  // Fetch points on mount
  useEffect(() => {
    fetchPoints();
  }, [fetchPoints]);

  // Redeem flow state
  const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false);
  const [userPoints, setUserPoints] = useState(0);

  const handleCloseVoucherModal = () => {
    setIsVoucherModalOpen(false);
    setSelectedVoucherId(null);
  };

  const handleRestaurantClick = () => {
    handleCloseVoucherModal();
  };

  // Redeem flow handlers
  const handleStartRedeem = (voucherId) => {
    setSelectedVoucherId(voucherId);
    setIsVoucherModalOpen(true);
  };

  const handleCloseRedeemModal = () => {
    setIsRedeemModalOpen(false);
  };

  const handleVoucherCreated = () => {
    // Refresh vouchers after creation
    fetchVouchers();
    // Refresh points after redemption
    fetchPoints();
  };

  return (
    <div className="page-content">
      <Container fluid>
        <div className="vouchers-page__header">
          <h1 className="vouchers-page__title">Vouchers</h1>

          <div className="vouchers-page__filters" role="tablist" aria-label="Filter vouchers">
            {FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                role="tab"
                aria-selected={filter.id === activeFilter}
                className={`vouchers-page__filter-btn ${
                  filter.id === activeFilter ? "vouchers-page__filter-btn--active" : ""
                }`}
                onClick={() => setActiveFilter(filter.id)}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="vouchers-grid">
            {[1, 2, 3, 4, 5, 6].map((index) => (
              <div key={index} className="voucher-card voucher-card--skeleton">
                <div className="voucher-card__stub">
                  <div className="skeleton-shimmer" />
                </div>
                <div className="voucher-card__body">
                  <div className="voucher-card__top">
                    <div className="voucher-card__amount skeleton-shimmer" />
                    <div className="voucher-card__pill skeleton-shimmer" />
                  </div>
                  <div className="voucher-card__bottom">
                    <div className="voucher-card__valid-at">
                      <div className="voucher-card__valid-label skeleton-shimmer" />
                      <div className="voucher-card__restaurant skeleton-shimmer" />
                    </div>
                    <div className="voucher-card__button skeleton-shimmer" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : userVouchers.length === 0 ? (
          <EmptyState
            title="No vouchers in this category yet"
            message="Start earning points by dining at partner restaurants to get vouchers!"
          />
        ) : (
          <>
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              style={{ overflowY: "auto", maxHeight: "calc(100vh - 210px)" }}
            >
              <div className="vouchers-grid">
              {userVouchers.map((voucher) => (
                <VoucherCard
                  key={voucher.id}
                  amount={voucher.amount}
                  status={voucher.status}
                  statusDate={voucher.statusDate}
                  restaurantName={voucher.restaurantName}
                  voucherCode={voucher.voucherId}
                  onRedeem={() => handleStartRedeem(voucher.id)}
                />
              ))}
              {loadingMore && (
                <>
                  {[1, 2, 3].map((index) => (
                    <div key={`skeleton-${index}`} className="voucher-card voucher-card--skeleton">
                      <div className="voucher-card__stub">
                        <div className="skeleton-shimmer" />
                      </div>
                      <div className="voucher-card__body">
                        <div className="voucher-card__top">
                          <div className="voucher-card__amount skeleton-shimmer" />
                          <div className="voucher-card__pill skeleton-shimmer" />
                        </div>
                        <div className="voucher-card__bottom">
                          <div className="voucher-card__valid-at">
                            <div className="voucher-card__valid-label skeleton-shimmer" />
                            <div className="voucher-card__restaurant skeleton-shimmer" />
                          </div>
                          <div className="voucher-card__button skeleton-shimmer" />
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
              </div>
            </div>
          </>
        )}
      </Container>

      <VoucherDetailModal
        voucherId={selectedVoucherId}
        isOpen={isVoucherModalOpen}
        onClose={handleCloseVoucherModal}
        onRestaurantClick={handleRestaurantClick}
      />

      <RedeemVoucherModal
        isOpen={isRedeemModalOpen}
        onClose={handleCloseRedeemModal}
        userPoints={userPoints}
        onVoucherCreated={handleVoucherCreated}
        onNavigateToWallet={handleNavigateToWallet}
      />
    </div>
  );
};

VouchersPage.propTypes = {
  vouchers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      status: PropTypes.oneOf(["active", "redeemed", "expired"]).isRequired,
      statusDate: PropTypes.string.isRequired,
      restaurantName: PropTypes.string.isRequired,
    })
  ),
  onRedeemVoucher: PropTypes.func,
  onVoucherClick: PropTypes.func,
};

VouchersPage.defaultProps = {
  onRedeemVoucher: () => {},
  onVoucherClick: () => {},
};

export default VouchersPage;
